import React, { useState, useMemo, useEffect } from 'react';
import Swal from 'sweetalert2';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { FaEye, FaTrashAlt, FaTimes, FaSearch, FaChevronDown, FaChevronUp, FaFileExcel } from 'react-icons/fa';
import { BsCheck2All } from 'react-icons/bs';
import './ViewContact.css';

import axiosInstance from '../../api/axiosInstance';

const ContactViewModal = ({ contact, onClose }) => {
    if (!contact) return null;
    const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'long', timeStyle: 'short' }) : 'N/A';
    
    return (
        <div className="view-contact-modal-overlay" onClick={onClose}>
            <div className="view-contact-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="view-contact-modal-header">
                    <h2>Submission Details</h2>
                    <button className="view-contact-modal-close-btn" onClick={onClose}><FaTimes /></button>
                </div>
                <div className="view-contact-modal-body">
                    <div className="view-contact-detail-item"><label>Name</label><span>{contact.name}</span></div>
                    <div className="view-contact-detail-item">
                        <label>Status</label>
                        <span className={`view-contact-status-badge ${contact.status.toLowerCase().replace(' ', '-')}`}>
                            {contact.status}
                        </span>
                    </div>
                    <div className="view-contact-detail-item"><label>Email Address</label><span>{contact.email}</span></div>
                    <div className="view-contact-detail-item"><label>Contact Number</label><span>{contact.phone}</span></div>
                    <div className="view-contact-detail-item view-contact-full-width"><label>Subject</label><span>{contact.subject}</span></div>
                    <div className="view-contact-detail-item view-contact-full-width">
                        <label>Message</label>
                        <p>{contact.message}</p>
                    </div>
                    <div className="view-contact-detail-item"><label>Submitted On</label><span>{formatDate(contact.createdAt)}</span></div>
                    {contact.resolvedOn && <div className="view-contact-detail-item"><label>Resolved On</label><span>{formatDate(contact.resolvedOn)}</span></div>}
                </div>
            </div>
        </div>
    );
};

const ViewContact = () => {
    const [contacts, setContacts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [expandedRow, setExpandedRow] = useState(null);
    const [viewingContact, setViewingContact] = useState(null);

    // --- FETCH FROM BACKEND ---
    const fetchContacts = async () => {
        try {
            const res = await axiosInstance.get('/api/contact/all');
            setContacts(res.data);
        } catch (error) {
            if (error.response?.status !== 401) {
                Swal.fire('Error', 'Failed to load contacts', 'error');
            }
        }
    };

    useEffect(() => {
        fetchContacts();
    }, []);

    const toggleRow = (id) => setExpandedRow(prev => (prev === id ? null : id));
    const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

    const filteredContacts = useMemo(() => {
        return contacts.filter(item => {
            if (statusFilter !== 'All' && item.status !== statusFilter) return false;
            const term = searchTerm.toLowerCase();
            return (item.name.toLowerCase().includes(term) || item.email.toLowerCase().includes(term) || item.subject.toLowerCase().includes(term));
        });
    }, [contacts, searchTerm, statusFilter]);

    // --- DESIGNED EXCEL EXPORT (EXCELJS) ---
    const handleExport = async () => {
        if (filteredContacts.length === 0) {
            return Swal.fire('Notice', 'No data to export.', 'info');
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Contact Submissions');

        // 1. Brand Title Block
        worksheet.mergeCells('A1:H1');
        worksheet.getCell('A1').value = 'SUCHETA KRIPLANI MAHAVIDYALAYA (S.K. COLLEGE)';
        worksheet.getCell('A1').font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FF1E3A8A' } }; // Royal Blue
        worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };

        // 2. Subtitle Block
        worksheet.mergeCells('A2:H2');
        worksheet.getCell('A2').value = 'Contact Submissions & Inquiries Directory (सम्पर्क पूछताछ विवरण तालिका)';
        worksheet.getCell('A2').font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FF2C3E50' } };
        worksheet.getCell('A2').alignment = { vertical: 'middle', horizontal: 'center' };

        // 3. Information Summary Block
        worksheet.mergeCells('A3:H3');
        worksheet.getCell('A3').value = `Generated on: ${new Date().toLocaleString('en-IN')}  |  Status Filter: ${statusFilter || 'All'}  |  Total Count: ${filteredContacts.length} records`;
        worksheet.getCell('A3').font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF64748B' } };
        worksheet.getCell('A3').alignment = { vertical: 'middle', horizontal: 'center' };

        worksheet.addRow([]); // Spacer Row

        // 4. Table Headers
        const headers = ["S.No", "Name", "Email Address", "Contact", "Subject", "Message", "Status", "Submitted On"];
        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } }; // Matching Royal Blue Header
            cell.font = { color: { argb: 'FFFFFFFF' }, bold: true, name: 'Arial', size: 10 };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border = { 
                top: { style: 'thin' }, 
                left: { style: 'thin' }, 
                bottom: { style: 'thin' }, 
                right: { style: 'thin' } 
            };
        });

        // 5. Data Rows Formatting
        filteredContacts.forEach((c, i) => {
            const formattedDate = c.createdAt ? new Date(c.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
            
            const row = worksheet.addRow([
                i + 1,
                c.name,
                c.email,
                c.phone || '—',
                c.subject || '—',
                c.message || '—',
                c.status,
                formattedDate
            ]);

            row.eachCell((cell, colIndex) => {
                cell.border = { 
                    top: { style: 'thin', color: { argb: 'FFE2E8F0' } }, 
                    left: { style: 'thin', color: { argb: 'FFE2E8F0' } }, 
                    bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } }, 
                    right: { style: 'thin', color: { argb: 'FFE2E8F0' } } 
                };
                cell.font = { name: 'Arial', size: 9 };

                // Align textual fields to the left, IDs & dates to the center
                if ([2, 3, 5, 6].includes(colIndex)) {
                    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                }
            });

            // Status Custom Color Rules (Column 7)
            const statusCell = row.getCell(7);
            let statusColor = 'FF1E2A78'; // Royal Blue Default
            if (c.status === 'Resolved') {
                statusColor = 'FF16A34A'; // Green
            } else if (c.status === 'In Progress') {
                statusColor = 'FFEA580C'; // Orange
            } else if (c.status === 'Open') {
                statusColor = 'FF1E40AF'; // Deep Blue
            }
            statusCell.font = { bold: true, name: 'Arial', size: 9, color: { argb: statusColor } };
        });

        // 6. Explicit Column Width Dimensions
        worksheet.columns = [
            { width: 8 },   // S.No
            { width: 24 },  // Name
            { width: 28 },  // Email Address
            { width: 16 },  // Contact
            { width: 28 },  // Subject
            { width: 55 },  // Message
            { width: 14 },  // Status
            { width: 22 }   // Submitted On
        ];

        // 7. Generate and download sheet
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, "SK_College_Inquiries_Report.xlsx");
    };

    const handleAction = async (type, contact) => {
        if (type === 'view') {
            if (contact.status === 'Open') {
                try {
                    await axiosInstance.put(`/api/contact/update/${contact._id}`, {
                        status: 'In Progress'
                    });
                    fetchContacts();
                } catch(e) {}
            }
            setViewingContact(contact);
        }

        else if (type === 'resolve') {
            Swal.fire({
                title: 'Mark as Resolved?',
                text: 'This will move the query to resolved status.',
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#1e3a8a'
            }).then(async (res) => {
                if (res.isConfirmed) {
                    try {
                        await axiosInstance.put(`/api/contact/update/${contact._id}`, {
                            status: 'Resolved'
                        });
                        fetchContacts();
                    } catch (error) {
                        if (error.response?.status !== 401) {
                            Swal.fire('Error', 'Update failed', 'error');
                        }
                    }
                }
            });
        }

        else if (type === 'delete') {
            Swal.fire({
                title: 'Delete entry?',
                text: 'This action cannot be undone.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#e11d48'
            }).then(async (res) => {
                if (res.isConfirmed) {
                    try {
                        await axiosInstance.delete(`/api/contact/delete/${contact._id}`);
                        fetchContacts();
                    } catch (error) {
                        if (error.response?.status !== 401) {
                            Swal.fire('Error', 'Delete failed', 'error');
                        }
                    }
                }
            });
        }
    };

    return (
        <div className="view-contact-wrapper">
            <div className="view-contact-container">
                <ContactViewModal contact={viewingContact} onClose={() => setViewingContact(null)} />
                
                <div className="view-contact-header">
                    <h1>Contact Submissions</h1>
                    <p>Manage and track student inquiries.</p>
                </div>

                <div className="view-contact-controls-row">
                    <div className="view-contact-search-wrapper">
                        <FaSearch className="view-contact-search-icon" />
                        <input type="text" placeholder="Search by name, email, or subject..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="view-contact-search-input" />
                    </div>
                    <div className="view-contact-filter-group">
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="view-contact-select">
                            <option value="All">All Status</option>
                            <option value="Open">Open</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Resolved">Resolved</option>
                        </select>
                        <button className="view-contact-export-btn" onClick={handleExport}><FaFileExcel /> Export</button>
                    </div>
                </div>

                <div className="view-contact-table-card">
                    <div className="view-contact-table-wrapper">
                        <table className="view-contact-table">
                            <thead>
                                <tr>
                                    <th className="view-contact-col-sno">S.No</th>
                                    <th className="view-contact-col-name">Student Name</th>
                                    <th className="view-contact-col-email">Email Address</th>
                                    <th className="view-contact-col-phone">Contact</th>
                                    <th className="view-contact-col-subject">Subject</th>
                                    <th className="view-contact-col-date">Submitted On</th>
                                    <th className="view-contact-col-status">Status</th>
                                    <th className="view-contact-col-actions">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredContacts.map((contact, index) => (
                                    <React.Fragment key={contact._id}>
                                        <tr className={`view-contact-row ${contact.status === 'Open' ? 'view-contact-row-unread' : ''}`}>
                                            <td className="view-contact-col-sno">{index + 1}</td>
                                            <td className="view-contact-col-name">
                                                <div className="view-contact-name-wrapper">
                                                    <span>{contact.name}</span>
                                                    <button className="view-contact-mobile-toggle-btn" onClick={() => toggleRow(contact._id)}>
                                                        {expandedRow === contact._id ? <FaChevronUp /> : <FaChevronDown />}
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="view-contact-col-email">{contact.email}</td>
                                            <td className="view-contact-col-phone">{contact.phone}</td>
                                            <td className="view-contact-col-subject">{contact.subject}</td>
                                            <td className="view-contact-col-date">{formatDate(contact.createdAt)}</td>
                                            <td className="view-contact-col-status">
                                                <span className={`view-contact-status-badge ${contact.status.toLowerCase().replace(' ', '-')}`}>{contact.status}</span>
                                            </td>
                                            <td className="view-contact-col-actions">
                                                <div className="view-contact-action-icons">
                                                    <button className="view-contact-icon-btn view" title="View Details" onClick={() => handleAction('view', contact)}><FaEye /></button>
                                                    {contact.status !== 'Resolved' ? 
                                                        <button className="view-contact-icon-btn resolve" title="Mark Resolved" onClick={() => handleAction('resolve', contact)}><BsCheck2All /></button> :
                                                        <button className="view-contact-icon-btn delete" title="Delete" onClick={() => handleAction('delete', contact)}><FaTrashAlt /></button>
                                                    }
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedRow === contact._id && (
                                            <tr className="view-contact-details-row">
                                                <td colSpan="8">
                                                    <div className="view-contact-details-content">
                                                        <div className="view-contact-details-grid">
                                                            <div className="view-contact-detail-card view-contact-dc-actions">
                                                                <div className="view-contact-detail-label">Actions</div>
                                                                <div className="view-contact-action-icons" style={{justifyContent: 'flex-start'}}>
                                                                    <button className="view-contact-icon-btn view" onClick={() => handleAction('view', contact)}><FaEye /></button>
                                                                    {contact.status !== 'Resolved' ? 
                                                                        <button className="view-contact-icon-btn resolve" onClick={() => handleAction('resolve', contact)}><BsCheck2All /></button> :
                                                                        <button className="view-contact-icon-btn delete" onClick={() => handleAction('delete', contact)}><FaTrashAlt /></button>
                                                                    }
                                                                </div>
                                                            </div>
                                                            <div className="view-contact-detail-card view-contact-dc-status">
                                                                <div className="view-contact-detail-label">Status</div>
                                                                <div className="view-contact-detail-value"><span className={`view-contact-status-badge ${contact.status.toLowerCase().replace(' ', '-')}`}>{contact.status}</span></div>
                                                            </div>
                                                            <div className="view-contact-detail-card view-contact-dc-email">
                                                                <div className="view-contact-detail-label">Email</div><div className="view-contact-detail-value">{contact.email}</div>
                                                            </div>
                                                            <div className="view-contact-detail-card view-contact-dc-phone">
                                                                <div className="view-contact-detail-label">Contact Number</div><div className="view-contact-detail-value">{contact.phone}</div>
                                                            </div>
                                                            <div className="view-contact-detail-card view-contact-dc-subject">
                                                                <div className="view-contact-detail-label">Subject</div><div className="view-contact-detail-value">{contact.subject}</div>
                                                            </div>
                                                            <div className="view-contact-detail-card view-contact-dc-date">
                                                                <div className="view-contact-detail-label">Submitted On</div><div className="view-contact-detail-value">{formatDate(contact.createdAt)}</div>
                                                            </div>
                                                            <div className="view-contact-detail-card view-contact-dc-message">
                                                                <div className="view-contact-detail-label">Message</div><div className="view-contact-text-content">{contact.message}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                                {filteredContacts.length === 0 && (
                                    <tr>
                                        <td colSpan="8" className="view-contact-no-data">No contact submissions found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewContact;